import { Injectable } from '@angular/core';
import { parseData } from '@ups/xplat/utils';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  IODataResult,
  ManpowerReqLogDto,
  ParentCustomerDto,
  ParentFacilityDto,
  PlantBasicLogDto,
  PlantDropdownDtoPascal,
  PlantDto,
  ProfileModel,
} from '@ups/xplat/api/dto';
import {
  MyHttpClientFactory,
  LocalStorageService,
  environment,
} from '@ups/xplat/core';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

const GET_PLANTS = '/api/plants-odata';
const GET_PLANTS_DROPDOWN = '/api/plants/dropdown';
const GET_PLANT = '/api/plant/{:plantID}';
const GET_PARENT_FACILITY_DROPDOWN = '/api/plants/parentFacilities';
const GET_DISA_OWNERS = '/api/plants/disaOwners';
const GET_DEFAULT_REQUIREMENTS = '/api/plant/defaultRequirements';
const SAVE_PLANT = '/api/plant';
const GET_PARENT_CUSTOMERS = '/api/parentCustomers-odata';
const SAVE_PARENT_CUSTOMER = '/api/parentCustomer';
const GET_PARENT_FACILITIES = '/api/parentFacilities-odata';
const SAVE_PARENT_FACILITY = '/api/parentFacility';
const GET_PARENT_CUSTOMER_DROPDOWN = '/api/parentCustomers';
const FAVORITE_FACILITY = '/api/parentFacilities/favorite';
const FAVORITE_CUSTOMER = '/api/parentCustomers/favorite';

@Injectable({ providedIn: 'root' })
export class PlantService {
  http: HttpClient;

  private profile: ProfileModel;

  constructor(
    private httpFactory: MyHttpClientFactory,
    private storage: LocalStorageService
  ) {
    this.http = httpFactory.createHttpClient(environment.urls.spartaAPI);
    this.profile = this.storage.getItem('profile') as ProfileModel;
  }

  search(searchString: string, params: HttpParams = null, limit: number = 50) {
    params = params || new HttpParams();
    params = params.append('searchString', searchString);
    params = params.append('take', limit);
    return this.http.get<PlantDropdownDtoPascal[]>(GET_PLANTS_DROPDOWN, {
      params,
    });
  }

  public getPlants(
    odatastring: string,
    isActive: boolean = true
  ): Observable<IODataResult<PlantDto>> {
    let url = GET_PLANTS;

    url += '?$format=json&$count=true';

    if (isActive) {
      url += '&isActive=' + isActive;
    }

    url += odatastring ? '&' + odatastring : '';

    return this.http.get<IODataResult<PlantDto>>(url).pipe(map(parseData));
  }

  public getPlant(plantID: number, createPlantCopy = false): Promise<PlantDto> {
    let url = GET_PLANT.split('{:plantID}').join(plantID.toString());

    const queryParameters: string[] = [];

    if (createPlantCopy === true) {
      queryParameters.push('createNewCopy=' + createPlantCopy);
    }

    if (queryParameters.length) {
      url = url + '?' + queryParameters.join('&');
    }

    return this.http.get<PlantDto>(url).toPromise().then(parseData);
  }
  public savePlant(model: PlantDto): Promise<PlantDto> {
    const url = SAVE_PLANT;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return this.http
      .post<PlantDto>(url, model, options)
      .toPromise()
      .then(parseData);
  }

  public getParentFacilityDropdown(
    searchString: string,
    take: number = 0,
    parentCustomerID: number = null,
    parentFacilityID: number = null
  ): Promise<ParentFacilityDto[]> {
    const url = GET_PARENT_FACILITY_DROPDOWN;
    let queryParams = '?';

    if (searchString) queryParams += 'searchString=' + searchString;

    if (parentCustomerID && parentCustomerID !== 0) {
      queryParams +=
        (queryParams.length > 1 ? '&' : '') +
        'parentCustomerID=' +
        parentCustomerID;
    }

    if (parentFacilityID && parentFacilityID !== 0) {
      queryParams +=
        (queryParams.length > 1 ? '&' : '') +
        'parentFacilityID=' +
        parentFacilityID;
    }

    if (take) {
      queryParams += (queryParams.length > 1 ? '&' : '') + 'take=' + take;
    }

    return this.http
      .get<ParentFacilityDto[]>(url + queryParams)
      .toPromise()
      .then(parseData);
  }

  public addOrRemoveFavoriteFacility(facilityID: string, auth0UserId?: string) {
    const url = FAVORITE_FACILITY;
    return this.http
      .post(url, {
        /* eslint-disable @typescript-eslint/naming-convention */
        UserID: auth0UserId ? auth0UserId : this.profile.auth0UserId,
        ParentFacilityID: facilityID,
        /* eslint-enable @typescript-eslint/naming-convention */
      })
      .toPromise();
  }

  public addOrRemoveFavoriteVPFacility(
    vpFacilityKeyID: string,
    auth0UserId?: string
  ) {
    const url = FAVORITE_FACILITY;
    return this.http
      .post(url, {
        /* eslint-disable @typescript-eslint/naming-convention */
        UserID: auth0UserId ? auth0UserId : this.profile.auth0UserId,
        VPParentFacilityKeyID: vpFacilityKeyID,
        /* eslint-enable @typescript-eslint/naming-convention */
      })
      .toPromise();
  }

  public getDisaOwners(
    searchString: string | null,
    take: number = 0
  ): Promise<unknown[]> {
    const url = GET_DISA_OWNERS;
    let queryParams = '?';

    if (searchString) queryParams += 'searchString=' + searchString;

    if (take) {
      queryParams += (queryParams.length > 1 ? '&' : '') + 'take=' + take;
    }

    return this.http
      .get<unknown[]>(url + queryParams)
      .toPromise()
      .then(parseData);
  }
  public getDefaultRequirements(): Promise<unknown[]> {
    const url = GET_DEFAULT_REQUIREMENTS;
    return this.http.get<unknown[]>(url).toPromise().then(parseData);
  }

  public getParentCustomers(odatastring: string) {
    let url = GET_PARENT_CUSTOMERS;
    url += '?$format=json&$count=true';

    url += odatastring ? '&' + odatastring : '';

    return this.http.get(url);
  }

  public saveParentCustomer(
    model: ParentCustomerDto
  ): Promise<ParentCustomerDto> {
    const url = SAVE_PARENT_CUSTOMER;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers };
    return this.http
      .post<ParentCustomerDto>(url, model, options)
      .toPromise()
      .then(parseData);
  }

  public getParentFacilities(odatastring: string) {
    let url = GET_PARENT_FACILITIES;
    url += '?$format=json&$count=true';

    url += odatastring ? '&' + odatastring : '';

    return this.http.get(url);
  }

  public saveParentFacility(
    model: ParentFacilityDto
  ): Promise<ParentFacilityDto> {
    const url = SAVE_PARENT_FACILITY;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers };
    return this.http
      .post<ParentFacilityDto>(url, model, options)
      .toPromise()
      .then(parseData);
  }

  public getParentCustomerDropdown(
    searchString: string,
    take: number = 0
  ): Promise<ParentCustomerDto[]> {
    const url = GET_PARENT_CUSTOMER_DROPDOWN;
    let queryParams = '?';

    if (searchString) queryParams += 'searchString=' + searchString;

    if (take) {
      queryParams += (queryParams.length > 1 ? '&' : '') + 'take=' + take;
    }

    return this.http
      .get<ParentCustomerDto[]>(url + queryParams)
      .toPromise()
      .then(parseData);
  }

  public addOrRemoveFavoriteCustomer(customerID: string) {
    const url = FAVORITE_CUSTOMER;
    return this.http
      .post(url, {
        /* eslint-disable @typescript-eslint/naming-convention */
        UserID: this.profile.auth0UserId,
        ParentCustomerID: customerID,
        /* eslint-enable @typescript-eslint/naming-convention */
      })
      .toPromise();
  }
  public addOrRemoveFavoriteVPCustomer(
    vpParentCustomerKeyID: string,
    auth0UserId?: string
  ) {
    const url = FAVORITE_CUSTOMER;
    return this.http
      .post(url, {
        /* eslint-disable @typescript-eslint/naming-convention */
        UserID: auth0UserId ? auth0UserId : this.profile.auth0UserId,
        VPParentCustomerKeyID: vpParentCustomerKeyID,
        /* eslint-enable @typescript-eslint/naming-convention */
      })
      .toPromise();
  }

  public getBasicLogHistory(plantId: number): Observable<PlantBasicLogDto[]> {
    return this.http.get<PlantBasicLogDto[]>(`api/plant/basicLog/${plantId}`);
  }

  public getManpowerReqLogHistory(
    plantId: number
  ): Observable<ManpowerReqLogDto[]> {
    return this.http.get<ManpowerReqLogDto[]>(
      `api/plant/manpowerReqLog/${plantId}`
    );
  }
}
